<template>
  <div v-loading="loading">
    <div class="detail-head">
      <div>
        <el-button type="text" icon="el-icon-arrow-left" @click="goBack">返回项目列表</el-button>
        <div class="head-title">
          <span class="name">{{ project.name || project.customerName || '-' }}</span>
          <el-tag size="mini" :type="statusType(projectStatusMap, project.status)">
            {{ statusLabel(projectStatusMap, project.status) }}
          </el-tag>
          <el-button
            v-if="project.status === 'DRAFT'"
            type="primary"
            size="mini"
            plain
            :loading="starting"
            @click="onStartProject"
          >开始</el-button>
          <el-button
            v-if="project.status === 'IN_PROGRESS'"
            type="success"
            size="mini"
            plain
            :loading="finishing"
            @click="onFinishProject"
          >竣工</el-button>
          <el-button
            v-if="project.status === 'COMPLETED'"
            type="warning"
            size="mini"
            plain
            :loading="reopening"
            @click="onReopenProject"
          >恢复进行中</el-button>
        </div>
        <div class="head-sub">{{ project.address || '-' }}</div>
      </div>
    </div>

    <el-alert
      v-if="caps.projectBindCustomer && project.needBindCustomer === 1 && !customerBound"
      class="bind-tip"
      type="warning"
      :closable="false"
      show-icon
    >
      <template slot="title">
        此项目还未绑定客户，项目经理在 App 中仅可查看，先生成邀请码，然后发给客户通过小程序绑定，
        <el-button type="text" class="invite-link" @click="goInvite">点击生成邀请码</el-button>
      </template>
    </el-alert>

    <el-row v-if="caps.projectProgress" :gutter="12" class="overview">
      <el-col :span="12">
        <el-card shadow="never">
          <div class="card-head">
            <span class="card-title">项目进度</span>
            <span class="card-extra">{{ doneStageCount }}/{{ stages.length }} 个工序</span>
          </div>
          <el-progress :percentage="progressPercent" :stroke-width="14" />
          <div class="metrics">
            <div class="metric">
              <span class="k">开工日期</span>
              <span class="v">{{ fmtDate(project.startDate) }}</span>
            </div>
            <div class="metric">
              <span class="k">实际开始</span>
              <span class="v">{{ fmtDate(project.actualStartDate) }}</span>
            </div>
            <div class="metric">
              <span class="k">竣工日期</span>
              <span class="v">{{ fmtDate(project.completionDate) }}</span>
            </div>
            <div class="metric">
              <span class="k">项目经理</span>
              <span class="v">{{ project.pmName || '-' }}</span>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card shadow="never">
          <div class="card-head">
            <span class="card-title">当前工序</span>
            <el-button type="text" @click="activeTab = 'stages'">查看计划</el-button>
          </div>
          <template v-if="currentStage">
            <div class="stage-name">
              {{ currentStage.stageName || '-' }}
              <el-tag size="mini" :type="statusType(stageStatusMap, currentStage.status)">
                {{ statusLabel(stageStatusMap, currentStage.status) }}
              </el-tag>
            </div>
            <div class="metrics">
              <div class="metric">
                <span class="k">计划日期</span>
                <span class="v">{{ fmtRange(currentStage.originPlanStart || currentStage.planStart, currentStage.originPlanEnd || currentStage.planEnd) }}</span>
              </div>
              <div class="metric">
                <span class="k">施工日期</span>
                <span class="v">{{ actualPlanText(currentStage) }}</span>
              </div>
              <div class="metric">
                <span class="k">施工人员</span>
                <span class="v">{{ currentStage.workerName || '暂未安排' }}</span>
              </div>
            </div>
          </template>
          <div v-else class="empty">暂无工序</div>
        </el-card>
      </el-col>
    </el-row>

    <el-tabs v-model="activeTab" type="border-card">
      <el-tab-pane label="项目档案" name="archive">
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="项目名称">{{ project.name || project.customerName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ project.customerPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目地址">{{ project.address || '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目来源">{{ project.sourceTypeName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目类型">{{ project.projectTypeName || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="caps.projectHouseType" label="房型">{{ project.houseTypeName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="装修面积">{{ project.area != null ? project.area + ' ㎡' : '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目金额">{{ project.money != null ? project.money + ' 元' : '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目经理">{{ project.pmName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ project.remark || '-' }}</el-descriptions-item>
        </el-descriptions>
        <div class="sub-title">项目附件</div>
        <div v-if="attachments.length" class="file-list">
          <a v-for="a in attachments" :key="a.id" class="file-link" :href="a.url" target="_blank">{{ a.fileName }}</a>
        </div>
        <div v-else class="empty">暂无附件</div>
      </el-tab-pane>

      <el-tab-pane v-if="caps.projectStages" label="项目计划" name="stages" lazy>
        <div v-if="planEditable" class="tab-toolbar tab-toolbar-right">
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddStage">新增工序</el-button>
        </div>
        <el-table :data="stages" border size="small" :row-class-name="stageRowClassName">
          <el-table-column prop="sortNo" label="排序" width="60" align="center" />
          <el-table-column prop="stageName" label="工序" min-width="130"/>
          <el-table-column label="计划日期" width="170">
            <template slot-scope="{ row }">{{ fmtRange(row.originPlanStart || row.planStart, row.originPlanEnd || row.planEnd) }}</template>
          </el-table-column>
          <el-table-column label="施工日期" width="170">
            <template slot-scope="{ row }">{{ actualPlanText(row) }}</template>
          </el-table-column>
          <el-table-column label="计划天数" width="80" align="center">
            <template slot-scope="{ row }">{{ planDays(row) }}</template>
          </el-table-column>
          <el-table-column label="施工天数" width="80" align="center">
            <template slot-scope="{ row }">{{ actualDays(row) }}</template>
          </el-table-column>
          <el-table-column label="工人" width="90">
            <template slot-scope="{ row }">{{ row.workerName || '-' }}</template>
          </el-table-column>
          <el-table-column label="确认状态" width="100">
            <template slot-scope="{ row }">
              <span class="worker-status-text" :class="'ws-' + (row.workerStatus || 'NONE')">
                {{ row.workerStatusName || workerStatusLabel(row) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="工序状态" width="80" fixed="right">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(stageStatusMap, row.status || 'PENDING')">
                {{ statusLabel(stageStatusMap, row.status || 'PENDING') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="计划状态" width="80" fixed="right">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="planScheduleStatus(row) === '延期' ? 'warning' : 'success'">
                {{ planScheduleStatus(row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="planEditable" label="操作" width="170" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                :disabled="!canStartStage(row) || stageActing"
                @click="onStartStage(row)"
              >开始</el-button>
              <el-button
                type="text"
                :disabled="!canCompleteStage(row) || stageActing"
                @click="onCompleteStage(row)"
              >完成</el-button>
              <el-button
                v-if="canEditStage(row)"
                type="text"
                @click="openEditStage(row)"
              >编辑</el-button>
              <el-button
                type="text"
                style="color:#ef4444"
                :disabled="!canDeleteStage(row)"
                @click="onDeleteStage(row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane v-if="caps.projectNotices" label="项目通知" name="notices" lazy>
        <el-table :data="notices" border size="small" v-loading="sectionLoading.notices">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column prop="stageName" label="工序" width="130" />
          <el-table-column prop="itemContent" label="通知内容" min-width="240" show-overflow-tooltip />
          <el-table-column prop="receiverName" label="接收人" min-width="140" show-overflow-tooltip />
          <el-table-column prop="confirmerName" label="确认人" width="130">
            <template slot-scope="{ row }">{{ row.confirmerName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="isConfirmName" label="确认状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.isConfirm === 1 ? 'success' : 'info'">{{ row.isConfirmName }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="发起人" width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="发起时间" width="160">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="材料申请" name="materialReqs" lazy>
        <div class="tab-toolbar">
          <el-radio-group v-model="viewMode.materialReqs" size="mini" @change="onViewChange('materialReqs')">
            <el-radio-button label="lines">明细</el-radio-button>
            <el-radio-button label="docs">单据</el-radio-button>
          </el-radio-group>
        </div>
        <el-table
          v-if="viewMode.materialReqs === 'lines'"
          :data="materialReqLines"
          border
          size="small"
          v-loading="sectionLoading.materialReqs"
        >
          <el-table-column prop="reqNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="stageName" label="工序" min-width="110" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column prop="materialName" label="材料" min-width="150" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="70" align="center">
            <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
          </el-table-column>
          <el-table-column label="申请数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.applyQty) }}</template>
          </el-table-column>
          <el-table-column label="已收数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.receivedQty) }}</template>
          </el-table-column>
          <el-table-column label="退货数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.returnedQty) }}</template>
          </el-table-column>
          <el-table-column label="实收数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.actualQty) }}</template>
          </el-table-column>
          <el-table-column label="审核状态" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(materialReqStatusMap, row.status)">
                {{ statusLabel(materialReqStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="160">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <el-table v-else :data="materialReqs" border size="small" v-loading="sectionLoading.materialReqs">
          <el-table-column prop="reqNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="stageName" label="工序" min-width="110" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column prop="totalAmount" label="金额" width="100" align="right" />
          <el-table-column label="审核状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(materialReqStatusMap, row.status)">
                {{ statusLabel(materialReqStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="收货状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(materialReceiveStatusMap, row.receiveStatus || 'NONE')">
                {{ statusLabel(materialReceiveStatusMap, row.receiveStatus || 'NONE') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="120">
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="收货单" name="receives" lazy>
        <div class="tab-toolbar">
          <el-radio-group v-model="viewMode.receives" size="mini" @change="onViewChange('receives')">
            <el-radio-button label="lines">明细</el-radio-button>
            <el-radio-button label="docs">单据</el-radio-button>
          </el-radio-group>
        </div>
        <el-table
          v-if="viewMode.receives === 'lines'"
          :data="receiveLines"
          border
          size="small"
          v-loading="sectionLoading.receives"
        >
          <el-table-column prop="receiveNo" label="收货单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="reqNo" label="申请单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column prop="materialName" label="材料" min-width="150" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="70" align="center">
            <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
          </el-table-column>
          <el-table-column label="收货数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.receiveQty) }}</template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="160">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <el-table v-else :data="receives" border size="small" v-loading="sectionLoading.receives">
          <el-table-column prop="receiveNo" label="收货单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="reqNo" label="申请单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
          <el-table-column prop="createdByName" label="创建人" width="120">
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="材料退货" name="returns" lazy>
        <div class="tab-toolbar">
          <el-radio-group v-model="viewMode.returns" size="mini" @change="onViewChange('returns')">
            <el-radio-button label="lines">明细</el-radio-button>
            <el-radio-button label="docs">单据</el-radio-button>
          </el-radio-group>
        </div>
        <el-table
          v-if="viewMode.returns === 'lines'"
          :data="returnLines"
          border
          size="small"
          v-loading="sectionLoading.returns"
        >
          <el-table-column prop="returnNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column prop="materialName" label="材料" min-width="150" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="70" align="center">
            <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
          </el-table-column>
          <el-table-column label="退货数量" width="90" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.qty) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(materialReturnStatusMap, row.status)">
                {{ statusLabel(materialReturnStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="160">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <el-table v-else :data="returns" border size="small" v-loading="sectionLoading.returns">
          <el-table-column prop="returnNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商" min-width="130" show-overflow-tooltip />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(materialReturnStatusMap, row.status)">
                {{ statusLabel(materialReturnStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
          <el-table-column prop="createdByName" label="创建人" width="120">
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="工资申请" name="labors" lazy>
        <div class="tab-toolbar">
          <el-radio-group v-model="viewMode.labors" size="mini" @change="onViewChange('labors')">
            <el-radio-button label="lines">明细</el-radio-button>
            <el-radio-button label="docs">单据</el-radio-button>
          </el-radio-group>
        </div>
        <el-table
          v-if="viewMode.labors === 'lines'"
          :data="laborLines"
          border
          size="small"
          v-loading="sectionLoading.labors"
        >
          <el-table-column prop="reqNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="stageName" label="工序" min-width="110" show-overflow-tooltip />
          <el-table-column prop="applyTypeName" label="申请类型" width="100" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.applyTypeName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="workerName" label="工人" min-width="110" show-overflow-tooltip />
          <el-table-column prop="itemName" label="工资事项" min-width="140" show-overflow-tooltip />
          <el-table-column prop="unit" label="单位" width="70" align="center">
            <template slot-scope="{ row }">{{ row.unit || '-' }}</template>
          </el-table-column>
          <el-table-column label="数量" width="80" align="right">
            <template slot-scope="{ row }">{{ fmtQty(row.qty) }}</template>
          </el-table-column>
          <el-table-column label="金额" width="100" align="right">
            <template slot-scope="{ row }">{{ fmtAmount(row.amount) }}</template>
          </el-table-column>
          <el-table-column label="审核状态" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborReqStatusMap, row.status)">
                {{ statusLabel(laborReqStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="发放状态" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborPayStatusMap, row.payStatus || 'NONE')">
                {{ statusLabel(laborPayStatusMap, row.payStatus || 'NONE') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="确认状态" width="100" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborConfirmStatusMap, row.confirmStatus || 'NONE')">
                {{ statusLabel(laborConfirmStatusMap, row.confirmStatus || 'NONE') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="110" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="160">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <el-table v-else :data="labors" border size="small" v-loading="sectionLoading.labors">
          <el-table-column prop="reqNo" label="单号" min-width="150" show-overflow-tooltip />
          <el-table-column prop="stageName" label="工序" min-width="110" show-overflow-tooltip />
          <el-table-column prop="applyTypeName" label="申请类型" width="100" />
          <el-table-column prop="totalAmount" label="金额" width="100" align="right" />
          <el-table-column label="审批状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborReqStatusMap, row.status)">
                {{ statusLabel(laborReqStatusMap, row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="发放状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborPayStatusMap, row.payStatus || 'NONE')">
                {{ statusLabel(laborPayStatusMap, row.payStatus || 'NONE') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="确认状态" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="statusType(laborConfirmStatusMap, row.confirmStatus || 'NONE')">
                {{ statusLabel(laborConfirmStatusMap, row.confirmStatus || 'NONE') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdByName" label="创建人" width="120">
            <template slot-scope="{ row }">{{ row.createdByName || '-' }}</template>
          </el-table-column>
          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane v-if="caps.projectSignins" label="项目签到" name="signins" lazy>
        <el-table :data="signins" border size="small" v-loading="sectionLoading.signins">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column label="签到时间" width="170">
            <template slot-scope="{ row }">{{ fmtDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="createdByName" label="人员" width="120" />
          <el-table-column label="签到方式" width="110">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="punchTagType(row.punchType)">
                {{ punchTypeLabel(row.punchType) || row.punchTypeName || '-' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="人员角色" width="100">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.signinType === 'SUPERVISOR' ? 'success' : (row.signinType === 'WORKER' ? 'warning' : '')">
                {{ signinTypeLabel(row.signinType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="genposition" label="地理位置" min-width="160" show-overflow-tooltip />
          <el-table-column prop="address" label="详细地址" min-width="200" show-overflow-tooltip />
        </el-table>
      </el-tab-pane>

      <el-tab-pane v-if="caps.projectPictures" label="现场照片" name="pictures" lazy>
        <div v-loading="sectionLoading.pictures">
          <div v-if="pictures.length" class="dynamic-list">
            <div v-for="p in pictures" :key="p.id" class="dynamic-item">
              <div class="dynamic-head">
                <span class="dynamic-stage">{{ p.stageName || '-' }}</span>
                <span class="dynamic-meta">{{ p.createdByName || '-' }} · {{ fmtDateTime(p.createdAt) }}</span>
              </div>
              <div class="dynamic-content">{{ p.content || '-' }}</div>
              <div v-if="(p.list || []).length" class="photo-list">
                <el-image
                  v-for="img in p.list"
                  :key="img.id"
                  class="photo"
                  :src="img.url"
                  :preview-src-list="(p.list || []).map(x => x.url)"
                  fit="cover"
                />
              </div>
            </div>
          </div>
          <div v-else class="empty">暂无现场照片</div>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="caps.projectMembers" label="项目成员" name="members" lazy>
        <el-table :data="members" border size="small">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column prop="displayName" label="成员" min-width="160" />
          <el-table-column prop="deptName" label="部门" width="140">
            <template slot-scope="{ row }">{{ row.deptName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="roleName" label="角色" width="140">
            <template slot-scope="{ row }">{{ row.roleName || '-' }}</template>
          </el-table-column>
          <el-table-column prop="phone" label="联系电话" width="150">
            <template slot-scope="{ row }">{{ row.phone || '-' }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="stageForm.id ? '编辑工序' : '新增工序'" :visible.sync="stageVisible" width="520px">
      <el-form :model="stageForm" label-width="130px">
        <el-form-item v-if="!stageForm.id" label="工序" required>
          <el-select v-model="stageForm.stageDefId" filterable style="width:100%" placeholder="请选择工序模板">
            <el-option v-for="s in stageDefs" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item v-else label="工序">
          <el-input :value="stageForm.stageName" disabled />
        </el-form-item>
        <el-form-item v-if="stageForm.id" label="计划日期" required>
          <el-date-picker
            v-model="stageForm.originRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            start-placeholder="开始"
            end-placeholder="结束"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item :label="stageForm.id ? '施工日期' : '计划日期'" required>
          <el-date-picker
            v-model="stageForm.planRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            start-placeholder="开始"
            end-placeholder="结束"
            style="width:100%"
          />
          <p v-if="!stageForm.id" class="tip">新增时仅写入计划日期；施工日期在点击开始/完成时自动填写。</p>
          <p v-else class="tip">施工日期为相对计划日期调整后的工期。</p>
        </el-form-item>
        <el-form-item v-if="stageForm.id" label="工序状态" required>
          <el-select v-model="stageForm.status" style="width:100%" placeholder="请选择工序状态">
            <el-option label="未开始" value="PENDING" />
            <el-option label="进行中" value="DOING" />
            <el-option label="已完成" value="DONE" />
          </el-select>
          <p class="tip">用于后台纠偏；可在项目经理操作后重新调整状态。</p>
        </el-form-item>
        <el-form-item label="排序" required>
          <el-input-number v-model="stageForm.sortNo" :min="0" :max="99999" controls-position="right" />
          <p class="tip">数字越小越靠前；列表按排序升序展示。</p>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="stageVisible=false">取消</el-button>
        <el-button type="primary" :loading="stageSaving" @click="saveStage">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getProject, listProjectNotices, listProjectPictures, listProjectSignins,
  listStageDefs, addProjectStage, updateProjectStagePlan, deleteProjectStage,
  reopenProject, startStage, completeStage, completeProject, startProject
} from '../../api/project'
import {
  listMaterialReqs, listMaterialReceives, listMaterialReturns,
  listMaterialReqProjectLineRows, listMaterialReceiveProjectLineRows,
  listMaterialReturnProjectLineRows
} from '../../api/supply'
import { listLaborReqs, listLaborReqProjectLineRows } from '../../api/labor'
import {
  projectStatusMap, stageStatusMap, constructionStatusMap,
  materialReqStatusMap, materialReceiveStatusMap, materialReturnStatusMap,
  laborReqStatusMap, laborPayStatusMap, laborConfirmStatusMap,
  statusLabel, statusType
} from '../../utils/status'
import { planCapabilities } from '../../utils/plan'
import { mapState } from 'vuex'

export default {
  name: 'ProjectDetail',
  data() {
    return {
      projectStatusMap,
      stageStatusMap,
      constructionStatusMap,
      materialReqStatusMap,
      materialReceiveStatusMap,
      materialReturnStatusMap,
      laborReqStatusMap,
      laborPayStatusMap,
      laborConfirmStatusMap,
      projectId: null,
      loading: false,
      reopening: false,
      finishing: false,
      starting: false,
      stageActing: false,
      activeTab: 'archive',
      project: {},
      stages: [],
      members: [],
      attachments: [],
      customerBound: false,
      notices: [],
      pictures: [],
      signins: [],
      materialReqs: [],
      materialReqLines: [],
      receives: [],
      receiveLines: [],
      returns: [],
      returnLines: [],
      labors: [],
      laborLines: [],
      viewMode: {
        materialReqs: 'lines',
        receives: 'lines',
        returns: 'lines',
        labors: 'lines'
      },
      loadedSections: {},
      sectionLoading: {},
      stageDefs: [],
      stageVisible: false,
      stageSaving: false,
      stageForm: {
        id: null,
        stageDefId: null,
        stageName: '',
        originRange: null,
        status: 'PENDING',
        sortNo: 10,
        planRange: null
      }
    }
  },
  computed: {
    ...mapState(['user']),
    caps() {
      return planCapabilities(this.user)
    },
    planEditable() {
      const s = this.project && this.project.status
      return s === 'DRAFT' || s === 'IN_PROGRESS'
    },
    doneStageCount() {
      return this.stages.filter(s => s.status === 'DONE').length
    },
    progressPercent() {
      if (!this.stages.length) return 0
      return Math.round((this.doneStageCount / this.stages.length) * 100)
    },
    currentStage() {
      if (!this.stages.length) return null
      return this.stages.find(s => s.status === 'DOING')
        || this.stages.find(s => s.status === 'PENDING')
        || this.stages[this.stages.length - 1]
    }
  },
  watch: {
    activeTab(name) {
      this.ensureTab(name)
    }
  },
  created() {
    this.projectId = Number(this.$route.query.id) || null
    if (!this.projectId) {
      this.$message.warning('缺少项目参数')
      this.goBack()
      return
    }
    const tab = this.$route.query.tab
    if (tab && typeof tab === 'string') {
      this.activeTab = tab
    }
    const allowed = {
      archive: true,
      members: this.caps.projectMembers,
      stages: this.caps.projectStages,
      notices: this.caps.projectNotices,
      pictures: this.caps.projectPictures,
      signins: this.caps.projectSignins,
      materialReqs: true,
      receives: true,
      returns: true,
      labors: true
    }
    if (!allowed[this.activeTab]) {
      this.activeTab = 'archive'
    }
    this.load()
  },
  methods: {
    statusLabel,
    statusType,
    fmtDate(v) {
      if (!v) return '-'
      return String(v).slice(0, 10)
    },
    fmtDateTime(v) {
      if (!v) return '-'
      return String(v).slice(0, 19).replace('T', ' ')
    },
    fmtRange(start, end) {
      if (!start && !end) return '-'
      return this.fmtDate(start) + ' 至 ' + this.fmtDate(end)
    },
    /** 施工日期展示文本 */
    actualPlanText(row) {
      if (!row) return '-'
      return this.fmtRange(row.planStart, row.planEnd)
    },
    daySpan(start, end) {
      const s = this.fmtDate(start)
      const e = this.fmtDate(end)
      if (s === '-' || e === '-') return '-'
      const a = new Date(s.replace(/-/g, '/'))
      const b = new Date(e.replace(/-/g, '/'))
      if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '-'
      const days = Math.round((b - a) / 86400000) + 1
      return days > 0 ? days : '-'
    },
    planDays(row) {
      return this.daySpan(row.originPlanStart || row.planStart, row.originPlanEnd || row.planEnd)
    },
    actualDays(row) {
      return this.daySpan(row.planStart, row.planEnd)
    },
    /** 施工开始不为空且晚于计划开始 → 延期，否则正常 */
    planScheduleStatus(row) {
      if (!row || !row.planStart) return '正常'
      const constructionStart = this.fmtDate(row.planStart)
      const planStart = this.fmtDate(row.originPlanStart || row.planStart)
      if (constructionStart === '-' || planStart === '-') return '正常'
      return constructionStart > planStart ? '延期' : '正常'
    },
    /** 与菜单「项目计划」工序状态计算一致 */
    stageRowClassName({ row }) {
      return row && row.status === 'DONE' ? 'stage-row-done' : ''
    },
    stageColorType(row) {
      if (!row) return 'grey'
      if (row.colorType) return row.colorType
      const planEnd = this.fmtDate(row.planEnd)
      const actualStart = this.fmtDate(row.actualStart)
      const today = this.fmtDate(new Date())
      if (actualStart !== '-' && planEnd !== '-') {
        return actualStart <= planEnd ? 'green' : 'yellow'
      }
      if (actualStart === '-' && planEnd !== '-' && planEnd < today) {
        return 'yellow'
      }
      return 'grey'
    },
    workerStatusLabel(row) {
      if (!row) return '-'
      if (row.workerStatusName) return row.workerStatusName
      const map = {
        NONE: '未选择',
        SELECTED: '已选待确认',
        CONFIRMED: '已确认',
        CANCEL_PENDING: '确认后取消中'
      }
      return map[row.workerStatus || 'NONE'] || '未选择'
    },
    canEditStage(row) {
      return this.planEditable && !!row
    },
    canDeleteStage(row) {
      if (!this.planEditable || !row) return false
      // 仅未开始可删；进行中、已完成不可删
      return row.status !== 'DOING' && row.status !== 'DONE'
    },
    canStartStage(row) {
      if (!this.planEditable || !row) return false
      return row.status !== 'DOING' && row.status !== 'DONE'
    },
    canCompleteStage(row) {
      if (!this.planEditable || !row) return false
      return row.status === 'DOING'
    },
    async onStartStage(row) {
      if (!this.canStartStage(row) || this.stageActing) return
      this.stageActing = true
      try {
        await startStage(row.id)
        this.$message.success('已开始')
        await this.reloadStages()
      } finally {
        this.stageActing = false
      }
    },
    async onCompleteStage(row) {
      if (!this.canCompleteStage(row) || this.stageActing) return
      try {
        await this.$confirm(`确认将工序「${row.stageName || ''}」标记为已完成？`, '完成工序', { type: 'warning' })
      } catch (e) {
        return
      }
      this.stageActing = true
      try {
        await completeStage(row.id)
        this.$message.success('已完成')
        await this.reloadStages()
      } finally {
        this.stageActing = false
      }
    },
    async onStartProject() {
      if (this.project.status !== 'DRAFT' || this.starting) return
      try {
        await this.$confirm('确认开始该项目？将记录实际开始日期，项目状态变为进行中。', '开始项目', {
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.starting = true
      try {
        await startProject(this.projectId)
        this.$message.success('项目已开始')
        await this.load()
      } finally {
        this.starting = false
      }
    },
    async onFinishProject() {
      if (this.project.status !== 'IN_PROGRESS' || this.finishing) return
      try {
        await this.$confirm('确认将项目标记为已竣工？竣工后不可再操作工序（可通过「恢复进行中」回退）。', '项目竣工', {
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.finishing = true
      try {
        await completeProject(this.projectId, true)
        this.$message.success('已竣工')
        await this.load()
      } finally {
        this.finishing = false
      }
    },
    async ensureStageDefs() {
      if (this.stageDefs.length) return
      const res = await listStageDefs({})
      this.stageDefs = (res.data || []).filter(s => s.enabled !== 0)
    },
    nextStageSortNo() {
      let max = 0
      for (const s of this.stages || []) {
        const n = Number(s.sortNo)
        if (!Number.isNaN(n) && n > max) max = n
      }
      return max + 10
    },
    async openAddStage() {
      if (!this.planEditable) return
      await this.ensureStageDefs()
      this.stageForm = {
        id: null,
        stageDefId: null,
        stageName: '',
        originRange: null,
        status: 'PENDING',
        sortNo: this.nextStageSortNo(),
        planRange: null
      }
      this.stageVisible = true
    },
    openEditStage(row) {
      if (!this.canEditStage(row)) return
      const day = v => {
        if (!v) return ''
        const s = String(v)
        return s.length >= 10 ? s.slice(0, 10) : s
      }
      const oStart = day(row.originPlanStart || row.planStart)
      const oEnd = day(row.originPlanEnd || row.planEnd)
      this.stageForm = {
        id: row.id,
        stageDefId: row.stageDefId,
        stageName: row.stageName || '',
        originRange: oStart && oEnd ? [oStart, oEnd] : null,
        status: row.status || 'PENDING',
        sortNo: row.sortNo != null ? Number(row.sortNo) : this.nextStageSortNo(),
        planRange: row.planStart && row.planEnd ? [day(row.planStart), day(row.planEnd)] : null
      }
      this.stageVisible = true
    },
    async saveStage() {
      if (!this.stageForm.id && !this.stageForm.stageDefId) {
        return this.$message.warning('请选择工序')
      }
      if (this.stageForm.sortNo == null || this.stageForm.sortNo === '') {
        return this.$message.warning('请填写排序')
      }
      if (this.stageForm.id && !this.stageForm.status) {
        return this.$message.warning('请选择工序状态')
      }
      const plan = this.stageForm.planRange || []
      if (!plan[0] || !plan[1]) {
        return this.$message.warning(this.stageForm.id ? '请填写施工日期' : '请填写计划日期')
      }
      let origin = null
      if (this.stageForm.id) {
        origin = this.stageForm.originRange || []
        if (!origin[0] || !origin[1]) {
          return this.$message.warning('请填写计划日期')
        }
      }
      this.stageSaving = true
      try {
        if (this.stageForm.id) {
          await updateProjectStagePlan(this.stageForm.id, {
            originPlanStart: origin[0],
            originPlanEnd: origin[1],
            planStart: plan[0],
            planEnd: plan[1],
            sortNo: this.stageForm.sortNo,
            status: this.stageForm.status
          })
        } else {
          await addProjectStage({
            projectId: this.projectId,
            stageDefId: this.stageForm.stageDefId,
            planStart: plan[0],
            planEnd: plan[1],
            sortNo: this.stageForm.sortNo
          })
        }
        this.$message.success('已保存')
        this.stageVisible = false
        await this.reloadStages()
      } finally {
        this.stageSaving = false
      }
    },
    onDeleteStage(row) {
      if (!this.canDeleteStage(row)) return
      this.$confirm(`确认删除工序「${row.stageName}」？`, '提示', { type: 'warning' })
        .then(async () => {
          await deleteProjectStage(row.id)
          this.$message.success('已删除')
          await this.reloadStages()
        })
        .catch(() => {})
    },
    onReopenProject() {
      if (this.project.status !== 'COMPLETED' || this.reopening) return
      this.$confirm('确认将项目恢复为「进行中」？将清空竣工日期，项目经理可继续在 App 中操作。', '恢复进行中', {
        type: 'warning'
      })
        .then(async () => {
          this.reopening = true
          try {
            await reopenProject(this.projectId)
            this.$message.success('已恢复为进行中')
            await this.load()
          } finally {
            this.reopening = false
          }
        })
        .catch(() => {})
    },
    async reloadStages() {
      const res = await getProject(this.projectId)
      const d = res.data || {}
      this.project = d.project || this.project
      this.stages = d.stages || []
    },
    fmtQty(v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return String(n)
    },
    fmtAmount(v) {
      if (v == null || v === '') return '-'
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return n.toFixed(2)
    },
    signinTypeLabel(t) {
      if (t === 'SUPERVISOR' || t === '1') return '监理'
      if (t === 'WORKER') return '工人'
      return '项目经理'
    },
    punchTypeLabel(t) {
      const map = { INSPECT: '巡检签到', SIGN_IN: '考勤签到', SIGN_OUT: '考勤签退' }
      return map[t] || ''
    },
    punchTagType(t) {
      if (t === 'SIGN_IN') return 'success'
      if (t === 'SIGN_OUT') return 'warning'
      return ''
    },
    async load() {
      this.loading = true
      try {
        const res = await getProject(this.projectId)
        const d = res.data || {}
        this.project = d.project || {}
        this.stages = d.stages || []
        this.members = d.members || []
        this.attachments = d.attachments || []
        this.customerBound = !!d.customerBound
        this.$set(this.loadedSections, 'archive', true)
        this.$set(this.loadedSections, 'members', true)
        this.$set(this.loadedSections, 'stages', true)
      } finally {
        this.loading = false
      }
    },
    ensureTab(name) {
      if (!name) return
      this.ensureSection(name)
    },
    onViewChange(key) {
      this.ensureSection(key)
    },
    sectionKey(tab) {
      const mode = this.viewMode[tab]
      if (mode === 'docs') return tab + ':docs'
      if (mode === 'lines') return tab + ':lines'
      return tab
    },
    ensureSection(key) {
      if (!key) return
      const loadKey = this.sectionKey(key)
      if (this.loadedSections[loadKey] || this.sectionLoading[key]) return
      const loaders = {
        notices: this.loadNotices,
        pictures: this.loadPictures,
        signins: this.loadSignins,
        materialReqs: this.loadMaterialReqs,
        receives: this.loadReceives,
        returns: this.loadReturns,
        labors: this.loadLabors
      }
      const fn = loaders[key]
      if (fn) fn()
    },
    async loadSection(key, requestFn, assignFn) {
      const loadKey = this.sectionKey(key)
      this.$set(this.sectionLoading, key, true)
      try {
        const res = await requestFn()
        assignFn(res.data || [])
        this.$set(this.loadedSections, loadKey, true)
      } finally {
        this.$set(this.sectionLoading, key, false)
      }
    },
    loadNotices() {
      return this.loadSection('notices', () => listProjectNotices(this.projectId), d => { this.notices = d })
    },
    loadPictures() {
      return this.loadSection('pictures', () => listProjectPictures(this.projectId), d => { this.pictures = d })
    },
    loadSignins() {
      return this.loadSection('signins', () => listProjectSignins({ projectId: this.projectId }), d => { this.signins = d })
    },
    loadMaterialReqs() {
      if (this.viewMode.materialReqs === 'docs') {
        return this.loadSection('materialReqs', () => listMaterialReqs({ projectId: this.projectId }), d => { this.materialReqs = d })
      }
      return this.loadSection('materialReqs', () => listMaterialReqProjectLineRows(this.projectId), d => { this.materialReqLines = d })
    },
    loadReceives() {
      if (this.viewMode.receives === 'docs') {
        return this.loadSection('receives', () => listMaterialReceives({ projectId: this.projectId }), d => { this.receives = d })
      }
      return this.loadSection('receives', () => listMaterialReceiveProjectLineRows(this.projectId), d => { this.receiveLines = d })
    },
    loadReturns() {
      if (this.viewMode.returns === 'docs') {
        return this.loadSection('returns', () => listMaterialReturns({ projectId: this.projectId }), d => { this.returns = d })
      }
      return this.loadSection('returns', () => listMaterialReturnProjectLineRows(this.projectId), d => { this.returnLines = d })
    },
    loadLabors() {
      if (this.viewMode.labors === 'docs') {
        return this.loadSection('labors', () => listLaborReqs({ projectId: this.projectId }), d => { this.labors = d })
      }
      return this.loadSection('labors', () => listLaborReqProjectLineRows(this.projectId), d => { this.laborLines = d })
    },
    goBack() {
      this.$router.push({ path: '/project' })
    },
    goInvite() {
      this.$router.push({ path: '/cms/invite' })
    }
  }
}
</script>

<style scoped>
.detail-head {
  margin-bottom: 12px;
}
.head-title { display: flex; align-items: center; gap: 8px; margin: 4px 0; }
.head-title .name { font-size: 18px; font-weight: 600; color: #1f2d3d; }
.head-sub { color: #909399; font-size: 13px; }
.bind-tip { margin-bottom: 12px; }
.bind-tip .invite-link {
  padding: 0;
  font-size: inherit;
  vertical-align: baseline;
  color: #e6a23c;
  font-weight: 600;
  text-decoration: underline;
}
.bind-tip .invite-link:hover {
  color: #cf9236;
}
.overview { margin-bottom: 12px; }
.card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.card-title { font-weight: 600; color: #1f2d3d; }
.card-extra { color: #909399; font-size: 13px; }
.stage-name { display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 600; color: #1f2d3d; }
.metrics { display: flex; gap: 24px; margin-top: 12px; flex-wrap: wrap; }
.metric { display: flex; flex-direction: column; gap: 4px; }
.metric .k { color: #909399; font-size: 12px; }
.metric .v { color: #1f2d3d; font-size: 13px; }
.sub-title { margin: 16px 0 8px; font-weight: 600; color: #1f2d3d; }
.tab-toolbar { display: flex; justify-content: flex-start; margin-bottom: 8px; align-items: center; gap: 8px; }
.tab-toolbar-right { justify-content: flex-end; }
.tab-toolbar-between { justify-content: space-between; }
.toolbar-tip { color: #909399; font-size: 12px; margin-left: 8px; }
.toolbar-tip.done { color: #067647; font-weight: 600; }
.tip { margin: 6px 0 0; font-size: 12px; color: #94a3b8; line-height: 1.4; }
.file-list { display: flex; flex-wrap: wrap; gap: 12px; }
.file-link { color: #409eff; text-decoration: none; font-size: 13px; }
.empty { padding: 24px 0; text-align: center; color: #909399; font-size: 13px; }
.dynamic-list { display: flex; flex-direction: column; gap: 12px; }
.dynamic-item { border: 1px solid #ebeef5; border-radius: 4px; padding: 12px; }
.dynamic-head { display: flex; align-items: center; gap: 8px; }
.dynamic-stage { font-weight: 600; color: #1f2d3d; }
.dynamic-meta { color: #909399; font-size: 12px; margin-left: auto; }
.dynamic-content { margin: 8px 0; color: #606266; font-size: 13px; white-space: pre-wrap; }
.photo-list { display: flex; flex-wrap: wrap; gap: 8px; }
.photo { width: 96px; height: 96px; border-radius: 4px; }
::v-deep .el-table .stage-row-done > td {
  background-color: #f5f7fa;
}
::v-deep .el-table .stage-row-done:hover > td {
  background-color: #eef1f6;
}
.worker-status-text {
  font-size: 13px;
  font-weight: 600;
}
.worker-status-text.ws-SELECTED {
  color: #e6a23c; /* 与工序「进行中」el-tag warning 一致 */
}
.worker-status-text.ws-CONFIRMED {
  color: #67c23a; /* 与工序「已完成」el-tag success 一致 */
}
.worker-status-text.ws-CANCEL_PENDING {
  color: #f56c6c;
}
.worker-status-text.ws-NONE {
  color: #909399;
}
</style>
